import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CheckIcon from "@mui/icons-material/Check";
import PersonIcon from "@mui/icons-material/Person";
import { formatName, get_t_str_minute_precision } from "../../../../helpers/helpers";

/**
 * Reusable modal component for displaying detailed survey information.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.survey - Survey data object
 * @param {Object} props.laborData - Optional labor data for staffing levels (schedule overlay only)
 */
function SurveyDetailModal(props) {
  const { open, onClose, survey, laborData } = props;

  if (!survey) return null;

  // Get color based on rating
  const getRatingColor = (rating) => {
    if (rating === 5) return "bg-green-500";
    if (rating === 4) return "bg-lime-500";
    if (rating === 3) return "bg-yellow-500";
    if (rating === 2) return "bg-orange-500";
    return "bg-red-500"; // 1
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={`full-${i}`} className="text-yellow-500" style={{ fontSize: '24px' }} />);
    }

    if (hasHalfStar) {
      stars.push(<StarHalfIcon key="half" className="text-yellow-500" style={{ fontSize: '24px' }} />);
    }

    const remainingStars = 5 - Math.ceil(rating);
    for (let i = 0; i < remainingStars; i++) {
      stars.push(<StarOutlineIcon key={`empty-${i}`} className="text-yellow-500" style={{ fontSize: '24px' }} />);
    }

    return stars;
  };

  // Get staffing levels at the time of a review (only if labor data provided)
  const getStaffingAtTime = (decimalHour) => {
    if (!laborData || !laborData.summary || laborData.summary.length === 0) {
      return null;
    }

    const { summary, label1, label2 } = laborData;

    // Find the closest PREVIOUS time point (at or before the review time)
    const validDataPoints = summary.filter(point => point.qh_num <= decimalHour);

    let closestDataPoint;
    if (validDataPoints.length === 0) {
      closestDataPoint = summary[0];
    } else {
      closestDataPoint = validDataPoints.reduce((prev, curr) => {
        return curr.qh_num > prev.qh_num ? curr : prev;
      });
    }

    const serviceTotal = closestDataPoint[`${label1} Service Total`] || 0;
    const supportTotal = closestDataPoint[`${label1} Support Total`] || 0;
    const scheduledTotal = closestDataPoint[`${label2} Labor Total`] || 0;
    const forecastedTotal = serviceTotal + supportTotal;
    const difference = scheduledTotal - forecastedTotal;

    return {
      time: closestDataPoint.time,
      serviceTotal,
      supportTotal,
      scheduledTotal,
      forecastedTotal,
      difference,
      status: difference === 0 ? 'perfect' : (difference < 0 ? 'understaffed' : 'overstaffed')
    };
  };

  const staffingInfo = laborData ? getStaffingAtTime(survey.business_decimal_hour ?? survey.decimal_hour) : null;

  const getSurveyEventDate = () => {
    const timestampDate = survey.timestamp?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return timestampDate ? timestampDate[0] : survey.date;
  };

  const formatSurveyDateTime = () => {
    const surveyEventDate = getSurveyEventDate();

    if (!surveyEventDate) {
      return get_t_str_minute_precision(survey.decimal_hour);
    }

    const [year, month, day] = surveyEventDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day);
    const weekday = localDate.toLocaleDateString('en-US', { weekday: 'short' });
    const monthName = localDate.toLocaleDateString('en-US', { month: 'short' });
    return `${weekday}, ${monthName} ${day} ${get_t_str_minute_precision(survey.decimal_hour)}`;
  };

  const formatSurveyTime = () => {
    return get_t_str_minute_precision(survey.decimal_hour);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="survey-detail-modal"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          border: "2px solid #E2E8F0",
          borderRadius: "15px",
          boxShadow: 24,
          p: 4,
        }}
      >
        <div
          className="absolute right-4 top-4 text-slate-500 cursor-pointer"
          onClick={onClose}
        >
          <CancelIcon />
        </div>

        <h2 className="font-bold text-2xl mb-4">Guest Survey Details</h2>

        {/* Rating display */}
        <div className="mb-4">
          <div
            className={`inline-flex items-center justify-center ${getRatingColor(
              survey.rating
            )} text-white rounded-lg px-4 py-2 text-2xl font-bold mb-2`}
          >
            {survey.rating}/5
          </div>
          <div className="flex items-center gap-1 mt-2">
            {renderStars(survey.rating)}
          </div>
        </div>

        {/* Timestamp and Source */}
        <div className="mb-4 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <strong>Time:</strong>
            <span>
              {formatSurveyDateTime()}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <strong>Source:</strong>
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
              {survey.source}
            </span>
            <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
              {survey.timing_type}
            </span>
          </div>
        </div>

        {/* Guest Comment */}
        {survey.feedback && (
          <div className="mb-4">
            <h3 className="font-bold text-lg mb-2">Guest Comment</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-slate-700 italic">"{survey.feedback}"</p>
            </div>
          </div>
        )}

        {/* Order Information (Toast API only) */}
        {survey.source === 'toast api' && survey.order_total && (
          <div className="mb-4">
            <h3 className="font-bold text-lg mb-2">Order Information</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              {/* Order metadata - only show if at least one exists */}
              {(survey.pos_order_number || survey.order_type) && (
                <div className="flex gap-4 mb-3 pb-3 border-b border-slate-200">
                  {survey.pos_order_number && (
                    <div>
                      <p className="text-xs text-slate-600">Order #</p>
                      <p className="text-sm font-semibold text-slate-900">{survey.pos_order_number}</p>
                    </div>
                  )}
                  {survey.order_type && (
                    <div>
                      <p className="text-xs text-slate-600">Type</p>
                      <p className="text-sm font-semibold text-slate-900 capitalize">{survey.order_type}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Items Ordered */}
              {survey.order_items && survey.order_items.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-slate-600 mb-2 font-semibold">Items Ordered</p>
                  <div className="space-y-2">
                    {survey.order_items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <div className="flex-1">
                          <span className="text-slate-900">{item.quantity}x {item.name}</span>
                          {item.modifiers && item.modifiers.length > 0 && (
                            <p className="text-xs text-slate-500 ml-4">
                              {item.modifiers.join(', ')}
                            </p>
                          )}
                        </div>
                        {item.price && (
                          <span className="text-slate-600 ml-2">${parseFloat(item.price).toFixed(2)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Financial breakdown */}
              <div className="border-t border-slate-200 pt-3">
                <div className="space-y-1 pb-2">
                  {survey.order_subtotal && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Subtotal</span>
                      <span className="text-slate-900">${parseFloat(survey.order_subtotal).toFixed(2)}</span>
                    </div>
                  )}
                  {survey.order_tax_amount && parseFloat(survey.order_tax_amount) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Tax</span>
                      <span className="text-slate-900">${parseFloat(survey.order_tax_amount).toFixed(2)}</span>
                    </div>
                  )}
                  {survey.order_tip_amount && parseFloat(survey.order_tip_amount) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Tip</span>
                      <span className="text-slate-900">${parseFloat(survey.order_tip_amount).toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-between text-base font-bold pt-2 border-t border-slate-300">
                  <span className="text-slate-900">Total</span>
                  <span className="text-slate-900">${parseFloat(survey.order_total).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Staff Working */}
        <div className="mb-4">
          <h3 className="font-bold text-lg mb-2">
            Staff Working at {formatSurveyTime()}
          </h3>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            {survey.staff_members && survey.staff_members.length > 0 ? (
              <div className="space-y-2">
                {survey.staff_members.map((staff, index) => (
                  <div key={index} className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{formatName(staff.name)}</span>
                      {staff.is_primary && (
                        <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-md font-medium">
                          Primary
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                      {staff.role || 'N/A'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-600 italic">No staff data available</p>
            )}
            <p className="text-xs text-slate-500 mt-3">
              Total staff on duty: {survey.staff_members?.length || 0}
            </p>
          </div>
        </div>

        {/* Staffing Levels at Review Time (only if labor data provided) */}
        {staffingInfo && (
          <div className="mb-4">
            <h3 className="font-bold text-lg mb-2">
              Staffing Levels at {formatSurveyTime()}
            </h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <div className="space-y-2 mb-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{laborData.label1} Service</span>
                  <span className="text-slate-900 font-medium">{staffingInfo.serviceTotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{laborData.label1} Support</span>
                  <span className="text-slate-900 font-medium">{staffingInfo.supportTotal}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-slate-300 pt-2">
                  <span className="text-slate-700 font-semibold">Total {laborData.label1}</span>
                  <span className="text-slate-900 font-semibold">{staffingInfo.forecastedTotal}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-700 font-semibold">{laborData.label2} Labor</span>
                  <span className="text-slate-900 font-semibold">{staffingInfo.scheduledTotal}</span>
                </div>
              </div>

              {/* Status indicator */}
              <div className="border-t border-slate-300 pt-3 mt-3">
                {staffingInfo.status === 'perfect' ? (
                  <div className="flex flex-col items-center justify-center text-emerald-600">
                    <div>
                      <CheckIcon style={{ fontSize: "32px" }} />
                    </div>
                    <div className="text-sm">Perfect</div>
                  </div>
                ) : staffingInfo.status === 'understaffed' ? (
                  <div className="flex flex-col items-center justify-center text-blue-600">
                    <div className="flex items-center space-x-1">
                      <div className="font-bold text-2xl">{Math.abs(staffingInfo.difference)}</div>
                      <div>
                        <PersonIcon
                          style={{ fontSize: "26px", marginBottom: "1px" }}
                        />
                      </div>
                    </div>
                    <div className="text-sm">Understaffed</div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center text-rose-600">
                    <div className="flex items-center space-x-1">
                      <div className="font-bold text-2xl">{Math.abs(staffingInfo.difference)}</div>
                      <div>
                        <PersonIcon
                          style={{ fontSize: "26px", marginBottom: "1px" }}
                        />
                      </div>
                    </div>
                    <div className="text-sm">Overstaffed</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Box>
    </Modal>
  );
}

export default SurveyDetailModal;
